import React, { useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { useHover } from 'javascripts/hooks/use-hover';
import { v4 as uuidv4 } from 'uuid';

import { AssetImg } from 'javascripts/components/asset_img';

import styles from 'stylesheets/components/chirp_button.module';

export function ChirpButton(props) {
  const [hoverRef, isHovered] = useHover();

  const buttonId = useMemo(() => `chirp-button-${uuidv4()}`, []);

  function displayStyle() {
    return isHovered && !_.isNil(props.displayStyleHover) ? props.displayStyleHover : props.displayStyle;
  }

  function renderIcon(alignIcon) {
    if (alignIcon !== props.alignIcon) {
      return null;
    }

    const icon = isHovered && !_.isNil(props.iconHover) ? props.iconHover : props.icon;
    if (_.isNil(icon)) {
      return null;
    }

    return <AssetImg src={icon} attrs={{ alt: '', className: classNames(styles.icon, styles[props.iconSize]) }} />;
  }

  function renderLabel() {
    const label = isHovered && !_.isNil(props.labelHover) ? props.labelHover : props.label;
    if (_.isNil(label)) {
      return null;
    }

    return <span id={buttonId}>{label}</span>;
  }

  function ariaLabelAttr() {
    const label = isHovered && !_.isNil(props.labelHover) ? props.labelHover : props.label;

    if (_.isNil(label)) {
      return { 'aria-label': props.iconAltText };
    }

    return {
      'aria-labelledby': buttonId,
    };
  }

  function renderButton() {
    const classes = classNames(
      styles.chirpButton,
      styles[displayStyle()],
      styles[props.displayType],
      styles[`${props.fontSize}Font`],
      props.class,
      {
        [styles.fullWidth]: props.fullWidth,
        [styles.round]: props.round,
        [styles.dropShadow]: isHovered && props.hoverDropShadow,
        [styles.fontUppercase]: props.fontUppercase,
      },
    );

    if (!_.isNil(props.href)) {
      return (
        <a
          className={classes}
          href={props.href}
          onClick={props.onClick}
          ref={hoverRef}
          {...props.attrs}
          {...ariaLabelAttr()}
        >
          {renderIcon('left')}
          {renderLabel()}
          {renderIcon('right')}
        </a>
      );
    }
    return (
      <button
        className={classes}
        onClick={props.onClick}
        disabled={props.isDisabled}
        ref={hoverRef}
        {...props.attrs}
        {...ariaLabelAttr()}
      >
        {renderIcon('left')}
        {renderLabel()}
        {renderIcon('right')}
      </button>
    );
  }

  return <>{renderButton()}</>;
}

ChirpButton.propTypes = {
  alignIcon: PropTypes.oneOf(['left', 'right']),
  attrs: PropTypes.object,
  class: PropTypes.string,
  displayStyle: PropTypes.oneOf(['gray', 'yellow', 'pink', 'bold', 'underline', 'navy', 'white', 'lightGray']),
  displayStyleHover: PropTypes.oneOf(['gray', 'yellow', 'pink', 'bold', 'underline', 'navy', 'white', 'lightGray']),
  displayType: PropTypes.oneOf(['button', 'link', 'pill', 'block', 'iconOnly']),
  fontSize: PropTypes.oneOf(['extraSmall', 'small', 'medium', 'large', 'extraLarge']),
  fontUppercase: PropTypes.bool,
  fullWidth: PropTypes.bool,
  href: PropTypes.string,
  hoverDropShadow: PropTypes.bool,
  icon: PropTypes.string,
  iconHover: PropTypes.string,
  iconAltText: PropTypes.string,
  iconSize: PropTypes.oneOf(['small', 'medium', 'large', 'extraLarge']),
  isDisabled: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.object]),
  labelHover: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onClick: PropTypes.func,
  round: PropTypes.bool,
};

ChirpButton.defaultProps = {
  alignIcon: 'left',
  attrs: {},
  displayStyle: 'gray',
  displayType: 'button',
  fontSize: 'medium',
  fontUppercase: false,
  fullWidth: false,
  hoverDropShadow: false,
  iconAltText: '',
  iconSize: 'medium',
  isDisabled: false,
  onClick: () => {},
  round: false,
};
